import { createContext, useContext, useMemo, useState } from 'react';
import { useQueryFetch } from 'sonata-react-components';
import { removeSonataSession } from 'sonata-static-storage/lib/helpers/session.helpers';

import { loadTaptapConfig } from '@/global/taptap';
import { setBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { removeSession, setSession } from '@/store/global/session/selectors';
import { setUser } from '@/store/global/user/selectors';
import { setUserRoles } from '@/store/global/userRoles/selectors';

type State = {
  status: 'authenticated' | 'unauthenticated';
};

type Context = {
  refetch?: ReturnType<typeof useQueryFetch>['refetch'];
  status: State['status'];
};

const AuthContext = createContext<Context>({
  status: 'unauthenticated'
});

export function AuthProvider({ children }: { children: React.ReactElement }) {
  const [state, setState] = useState<State>({
    status: 'unauthenticated'
  });

  const token: Record<string, any> = {};
  const { isLoading, refetch } = useQueryFetch(
    ['authenticate'],
    async () => {
      await loadTaptapConfig();
      const { msg } = setSession();

      if (msg === 'ok') {
        if (state.status === 'unauthenticated') {
          await Promise.all([setUser(token), setUserRoles(token)]);
          await setBaseCurrency(token);
        }

        setState(prevState => ({
          ...prevState,
          status: 'authenticated'
        }));
      } else {
        removeSession();
        removeSonataSession();
        setState(prevState => ({
          ...prevState,
          status: 'unauthenticated'
        }));
      }
    },
    {
      enabled: true
    }
  );

  const value = useMemo(
    () => ({
      refetch,
      status: state.status
    }),
    [state]
  );

  if (!isLoading) {
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  return null;
}

export function useAuth() {
  return useContext(AuthContext);
}
